/**
 * br-reforma-video-statement — reveal and accessible video control.
 *
 * Re-implements Reforma action list a-115: SCROLL_INTO_VIEW with a
 * 10% offset, blur(12px) + opacity 0, then a 400ms delay and 500ms
 * reveal. Hidden state is JS-armed only, so no-JS remains readable.
 * Reduced motion skips the reveal and pauses the video on its poster.
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const setup = (section) => {
    if (section.dataset.brReformaVideoStatementReady === 'true') return;
    section.dataset.brReformaVideoStatementReady = 'true';

    const content = section.querySelector('.br-reforma-video-statement__content');

    if (content && !reducedMotion.matches && 'IntersectionObserver' in window) {
      section.classList.add('br-reforma-video-statement--armed');

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            section.classList.remove('br-reforma-video-statement--armed');
            observer.disconnect();
          }
        },
        { rootMargin: '0px 0px -10% 0px' }
      );

      observer.observe(content);
    }

    const video = section.querySelector('.br-reforma-video-statement__video');
    const toggle = section.querySelector('[data-br-reforma-video-statement-toggle]');
    if (!(video instanceof HTMLVideoElement) || !toggle) return;

    const pauseIcon = toggle.querySelector('[data-br-reforma-video-statement-pause]');
    const playIcon = toggle.querySelector('[data-br-reforma-video-statement-play]');
    const labels = {
      play: toggle.dataset.labelPlay,
      pause: toggle.dataset.labelPause,
    };
    let userPaused = false;

    const syncControl = () => {
      const paused = video.paused;
      pauseIcon.hidden = paused;
      playIcon.hidden = !paused;
      toggle.setAttribute('aria-pressed', paused ? 'false' : 'true');
      toggle.setAttribute('aria-label', paused ? labels.play : labels.pause);
    };

    const syncPlayback = () => {
      if (reducedMotion.matches || userPaused) {
        video.pause();
      } else {
        video.play().catch(syncControl);
      }

      syncControl();
    };

    toggle.addEventListener('click', () => {
      if (video.paused) {
        userPaused = false;
        video.play().catch(syncControl);
      } else {
        userPaused = true;
        video.pause();
      }
    });

    video.addEventListener('play', syncControl);
    video.addEventListener('pause', syncControl);
    reducedMotion.addEventListener('change', syncPlayback);
    // Sources are deferred (data-src) and attach on approach; syncPlayback
    // starts playback once they do. Until then the poster holds.
    window.brVideoDefer?.attach(video, { onAttached: syncPlayback });
    syncPlayback();
  };

  document.querySelectorAll('[data-br-reforma-video-statement]').forEach(setup);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-br-reforma-video-statement]').forEach(setup);
  });
})();
